import { makeAutoObservable, runInAction } from 'mobx';
import { inject, injectable } from 'inversify';
import logger from 'js-logger';
import { getLastPlanOrCreateNew } from '@shared/api/osi/accruals';
import { getErrorText, notifications } from '@shared/utils/notifications';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';

// OSI-823: значения Osi.PrintMonth, от которых зависит текст подсказки «Как считается пеня».
// Словарь на бэке (OSI.Core, Osi.PrintMonth): 1 — «Будущий», 0 — «Текущий» (месяц в месяц, дефолт), -1 — «Предыдущий».
// Подсказка написана только для двух схем: 0 — «месяц в месяц», -1 — «за предыдущий месяц».
// Для 1 («Будущий») и пока osiInfo не загружен (undefined) — кнопку НЕ показываем.
const PRINT_MONTH_CURRENT = 0;
const PRINT_MONTH_PREVIOUS = -1;

// OSI-823: вариант текста подсказки по схеме выставления начислений дома
export type PenaltiesHelpMode = 'currentMonth' | 'prevMonth';

@injectable()
export class ViewModel {
  isOpenModal = false;

  isLoading = false;

  // OSI-823: день начисления из плана (accuralJobAtDay); null — ещё не загружен или загрузка не удалась
  accrualDay: number | null = null;

  // OSI-823: текст ошибки загрузки плана для показа внутри модалки (null — ошибки нет)
  loadError: string | null = null;

  // OSI-823: счётчик запросов — чтобы ответ старого запроса (модалку закрыли и сразу открыли заново)
  // не перетёр результат свежего
  private requestSeq = 0;

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService) {
    makeAutoObservable(this);
  }

  // OSI-823: реактивный геттер — osiInfo загружается асинхронно, кнопка появится сама после его прихода.
  // Строгое сравнение: любое значение кроме 0 / -1 (в т.ч. undefined) → null → кнопку не показываем.
  get mode(): PenaltiesHelpMode | null {
    const printMonth = this.osiModule.osiInfo?.printMonth;
    if (printMonth === PRINT_MONTH_CURRENT) return 'currentMonth';
    if (printMonth === PRINT_MONTH_PREVIOUS) return 'prevMonth';
    return null;
  }

  // OSI-823: день начисления подставляем в текст только если он реально получен (не пустой и не нулевой)
  get hasAccrualDay(): boolean {
    return this.accrualDay !== null && this.accrualDay > 0;
  }

  // OSI-823: день начисления грузим лениво при КАЖДОМ открытии модалки — тем же API, что AccuralDateViewModel,
  // чтобы день был актуален, если пользователь только что сменил его селектом «Изменить на».
  openModal = () => {
    this.isOpenModal = true;
    void this.loadAccrualDay();
  };

  closeModal = () => {
    this.isOpenModal = false;
  };

  loadAccrualDay = async () => {
    this.requestSeq += 1;
    const seq = this.requestSeq;
    this.isLoading = true;
    this.accrualDay = null;
    this.loadError = null;

    try {
      // OSI-823: osiId берём так же, как AccuralDateViewModel.loadPlan
      if (!this.osiModule.osiInfo?.id) throw new Error('osiId is undefined');
      const plan = await getLastPlanOrCreateNew(this.osiModule.osiInfo.id);
      if (seq !== this.requestSeq) return;

      const day = plan?.accuralJobAtDay ?? 0;
      if (!day) throw new Error('accuralJobAtDay is empty');

      runInAction(() => {
        this.accrualDay = day;
      });
    } catch (e) {
      if (seq !== this.requestSeq) return;
      logger.error(e);
      // OSI-823: строку-бизнес-ошибку бэка (интерцептор кидает message строкой) показываем как есть;
      // технические Error (osiId/accuralJobAtDay/транспорт) — общим common:error
      const backendText = typeof e === 'string' ? getErrorText(e) : undefined;
      notifications.error(backendText);
      runInAction(() => {
        this.loadError = backendText ?? null;
        this.accrualDay = null;
      });
    } finally {
      if (seq === this.requestSeq) {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    }
  };
}
